import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Grid, TextField, Typography, Button, Box, Paper, CircularProgress } from "@mui/material";
import { Save, Check } from "@mui/icons-material";

import Layout from "../components/Layout/Layout";
import QRReaderDialog from "../components/custom/QRReaderDialog";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { UserDataContext } from "../app/App";
import { useFetchEquipmentByIdMutation, useFetchGetEquipmentByNumberMutation, useCreateWorkshopOrderMutation } from "../api";

function ReportBroken() {
    const [equipment, setEquipment] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [issue, setIssue] = useState("");

    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const { user } = useContext(UserDataContext);

    const {
        mutate: fetchEquipmentById,
        isLoading: isEquipmentByIdLoading,
        isError: isEquipmentByIdError,
    } = useFetchEquipmentByIdMutation({
        onSuccess: (equipment) => {
            setEquipment(equipment);
            setIsDialogOpen(false);
        },
        onError: () => onFetchEquipmentError(),
    });
    const {
        mutate: fetchGetEquipmentByNumber,
        isLoading: isGetEquipmentByNumberLoading,
        isError: isGetEquipmentByNumberError,
    } = useFetchGetEquipmentByNumberMutation({
        onSuccess: (equipment) => {
            setEquipment(equipment);
            setIsDialogOpen(false);
        },
        onError: () => onFetchEquipmentError(),
    });

    const {
        mutate: createWorkshopOrder,
        isLoading: isWorkshopOrderLoading,
        isSuccess: isWorkshopOrderSuccess,
    } = useCreateWorkshopOrderMutation({
        onSuccess: () => {
            enqueueSnackbar("Report submitted successfully", {
                variant: "success",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 4000,
            });
            history.replace("/");
        },
        onError: () => {
            enqueueSnackbar("Error occurred while submitting the report", {
                variant: "error",
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
                autoHideDuration: 3000,
            });
        },
    });

    const onFetchEquipmentError = () => {
        enqueueSnackbar("Oops, that number didn't work. Try again?", {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 3000,
            preventDuplicate: true,
        });
    };

    const handleSubmitReport = () => {
        if (equipment && issue.trim() !== "") {
            createWorkshopOrder({
                Description: issue,
                VehicleId: equipment.id,
                ReporterId: user.id,
            });
        }
    };

    const isEquipmentLoading = isEquipmentByIdLoading || isGetEquipmentByNumberLoading;
    const isEquipmentError = isEquipmentByIdError || isGetEquipmentByNumberError;

    const isSubmitReportDisabled = !equipment || issue.trim() === "";
    const equipmentType = equipment?.vehicleType?.type || equipment?.vehicleType?.name || equipment?.vehicleType?.brandName || "-";

    return (
        <>
            <Layout color="rgb(173, 0, 0)" title="Report Broken" subtitle={user.name}>
                <Grid container direction="column" spacing={4}>
                    {/* Scan serial number */}
                    <Grid item container direction="column" spacing={1}>
                        <Grid item>
                            <Typography variant="h5" gutterBottom style={{ paddingBottom: "8px" }}>{`Something Broke?`}</Typography>
                        </Grid>
                        <Grid item>
                            <Button onClick={() => setIsDialogOpen(true)} variant="outlined" color="primary" size="large" fullWidth startIcon={<QrCodeScannerIcon />}>
                                {`Scan QR Code`}
                            </Button>
                        </Grid>
                        <Grid item>
                            {/* Vehicle details */}
                            <Box component={Paper} variant="outlined" display="flex" flexDirection="column" elevation={0} p={2} mt={1}>
                                <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                                    <Typography color="textSecondary">{`Type`}</Typography>
                                    <Typography>{equipmentType}</Typography>
                                </Box>

                                <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                                    <Typography color="textSecondary">{`Number`}</Typography>
                                    <Typography>{equipment && equipment.number ? equipment.number : "-"}</Typography>
                                </Box>

                                <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                                    <Typography color="textSecondary">{`Condition`}</Typography>
                                    <Typography>{equipment && equipment.condition ? equipment.condition : "-"}</Typography>
                                </Box>
                            </Box>

                            {!equipment && `Scan or input equipment number.`}
                        </Grid>
                    </Grid>

                    {/* Report issue */}
                    <Grid item container direction="column" spacing={2}>
                        {/* Issue text field */}
                        <Grid item>
                            <TextField
                                value={issue}
                                onChange={(e) => setIssue(e.target.value)}
                                disabled={isWorkshopOrderSuccess || !equipment}
                                variant={!equipment ? "filled" : "outlined"}
                                label="Issue"
                                placeholder="What is wrong?"
                                InputLabelProps={{ shrink: true }}
                                minRows={4}
                                multiline
                                fullWidth
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item>
                            <Button
                                onClick={handleSubmitReport}
                                disabled={isSubmitReportDisabled || isWorkshopOrderLoading}
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                style={{ borderRadius: 50 }}
                            >
                                <Box display="flex" alignItems="center">
                                    {isWorkshopOrderLoading ? (
                                        <CircularProgress color="inherit" size={24} />
                                    ) : (
                                        <>{isWorkshopOrderSuccess ? <Check style={{ marginRight: 8 }} /> : <Save style={{ marginRight: 8 }} />}</>
                                    )}
                                </Box>
                                {isWorkshopOrderSuccess ? "Report submitted" : "Submit report"}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Layout>

            <QRReaderDialog
                open={isDialogOpen}
                isLoading={isEquipmentLoading}
                isError={isEquipmentError}
                onClose={() => setIsDialogOpen(false)}
                onQRCodeScanSuccess={fetchEquipmentById}
                onManualSuccess={fetchGetEquipmentByNumber}
            />
        </>
    );
}

export default ReportBroken;
